import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { SalesLayout } from '@/components/SalesLayout';
import { Mail, MessageSquare, Plus, Edit2, Trash2, Copy, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Placeholders for Sales/Booking templates
const PLACEHOLDER_GROUPS = [
  { group: 'Customer', items: [
    { code: '{{customer_name}}', description: 'Customer full name' },
    { code: '{{first_name}}', description: 'First name' },
    { code: '{{last_name}}', description: 'Last name' },
    { code: '{{salutation}}', description: 'Salutation' },
    { code: '{{mobile}}', description: 'Mobile number' },
    { code: '{{whatsapp}}', description: 'WhatsApp number' },
    { code: '{{email}}', description: 'Email address' },
    { code: '{{address}}', description: 'Full address' },
    { code: '{{city}}', description: 'City' },
    { code: '{{aadhar_no}}', description: 'Aadhaar number' },
    { code: '{{pan_no}}', description: 'PAN number' },
  ]},
  { group: 'Vehicle', items: [
    { code: '{{brand}}', description: 'Vehicle brand' },
    { code: '{{model}}', description: 'Vehicle model' },
    { code: '{{variant}}', description: 'Vehicle variant' },
    { code: '{{color}}', description: 'Vehicle color' },
    { code: '{{ex_showroom_price}}', description: 'Ex-showroom price' },
    { code: '{{on_road_price}}', description: 'On-road price' },
    { code: '{{chassis_no}}', description: 'Chassis number' },
    { code: '{{engine_no}}', description: 'Engine/Motor number' },
    { code: '{{battery_no}}', description: 'Battery number' },
    { code: '{{key_no}}', description: 'Key number' },
  ]},
  { group: 'Booking', items: [
    { code: '{{booking_no}}', description: 'Booking number' },
    { code: '{{booking_date}}', description: 'Date of booking' },
    { code: '{{delivery_date}}', description: 'Delivery date' },
    { code: '{{sales_rep}}', description: 'Sales representative' },
    { code: '{{showroom}}', description: 'Showroom/dealer name' },
    { code: '{{dms_invoice_no}}', description: 'DMS Invoice number' },
  ]},
  { group: 'Registration & RTO', items: [
    { code: '{{registration_no}}', description: 'Registration number' },
    { code: '{{registration_date}}', description: 'Date of registration' },
    { code: '{{rto_code}}', description: 'RTO code' },
    { code: '{{hsrp_number_plate}}', description: 'HSRP number plate' },
  ]},
  { group: 'Finance', items: [
    { code: '{{financier_name}}', description: 'Financier name' },
    { code: '{{loan_amount}}', description: 'Loan amount' },
    { code: '{{loan_status}}', description: 'Loan status' },
    { code: '{{finance_payment_status}}', description: 'Payment status' },
  ]},
  { group: 'Insurance', items: [
    { code: '{{policy_no}}', description: 'Insurance policy number' },
    { code: '{{insurance_type}}', description: 'Type of insurance' },
    { code: '{{premium}}', description: 'Premium amount' },
    { code: '{{od_expiry}}', description: 'OD expiry date' },
    { code: '{{tp_expiry}}', description: 'TP expiry date' },
    { code: '{{nominee_name}}', description: 'Nominee name' },
  ]},
  { group: 'Company', items: [
    { code: '{{company_name}}', description: 'Dealership name' },
    { code: '{{company_phone}}', description: 'Dealership phone' },
    { code: '{{company_address}}', description: 'Dealership address' },
  ]},
  { group: 'Outstanding / Balance', items: [
    { code: '{{customer_balance}}', description: 'Customer Balance (₹) — for payment reminders & refund notices' },
  ]},
  // iter-121: Service-aligned customer detail placeholders. These mirror the
  // 14 booking fields used by the Service module so templates can be authored
  // once with consistent keys across both modules. Existing placeholders are
  // preserved unchanged for backward compatibility.
  { group: 'Customer Details (Service Aligned)', items: [
    { code: '{{building_name}}', description: 'Building name' },
    { code: '{{door_no}}', description: 'Door number' },
    { code: '{{address_1}}', description: 'Address line 1' },
    { code: '{{address_2}}', description: 'Address line 2' },
    { code: '{{address_3}}', description: 'Address line 3' },
    { code: '{{area}}', description: 'Area / locality' },
    { code: '{{state}}', description: 'State' },
    { code: '{{pin_code}}', description: 'Pincode' },
    { code: '{{insurance_company_name}}', description: 'Insurance company' },
    { code: '{{own_damage_expiry_date}}', description: 'Own damage expiry date' },
    { code: '{{dms_invoice_date}}', description: 'DMS invoice date' },
  ]},
];

function SalesTemplateManagement({ user, onLogout }) {
  const [templates, setTemplates] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState('email');
  const [showModal, setShowModal] = React.useState(false);
  const [editingTemplate, setEditingTemplate] = React.useState(null);
  const [formData, setFormData] = React.useState({
    name: '',
    type: 'email',
    subject: '',
    body: '',
    description: '',
    category: 'sales' // Add category to distinguish from leads templates
  });

  React.useEffect(() => {
    fetchTemplates();
  }, []);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/templates?category=sales`, getAuthHeader());
      setTemplates(response.data);
    } catch (error) {
      // If category filter not supported, fetch all and filter
      try {
        const response = await axios.get(`${BACKEND_URL}/api/templates`, getAuthHeader());
        setTemplates(response.data.filter(t => t.category === 'sales' || !t.category));
      } catch (err) {
        toast.error('Failed to load templates');
      }
    }
    setLoading(false);
  };

  const openCreateModal = (type) => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      type: type,
      subject: '',
      body: '',
      description: '',
      category: 'sales'
    });
    setShowModal(true);
  };

  const openEditModal = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      type: template.type,
      subject: template.subject || '',
      body: template.body,
      description: template.description || '',
      category: 'sales'
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.body) {
      toast.error('Name and Body are required');
      return;
    }

    try {
      if (editingTemplate) {
        await axios.put(`${BACKEND_URL}/api/templates/${editingTemplate.id}`, formData, getAuthHeader());
        toast.success('Template updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/templates`, formData, getAuthHeader());
        toast.success('Template created successfully');
      }
      setShowModal(false);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/templates/${id}`, getAuthHeader());
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const copyPlaceholder = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied ${code}`);
  };

  const emailTemplates = templates.filter(t => t.type === 'email');
  const whatsappTemplates = templates.filter(t => t.type === 'whatsapp');

  return (
    <SalesLayout user={user} onLogout={onLogout} title="Templates">
      <div className="max-w-6xl mx-auto p-4">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Customize Templates</h1>
          <p className="text-sm text-gray-500">Create and manage Email & WhatsApp message templates for Sales communications</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('email')}
            className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
              activeTab === 'email' 
                ? 'bg-[#F4ACB7] text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Mail className="w-4 h-4" />
            Email Templates ({emailTemplates.length})
          </button>
          <button
            onClick={() => setActiveTab('whatsapp')}
            className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
              activeTab === 'whatsapp' 
                ? 'bg-green-500 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            WhatsApp Templates ({whatsappTemplates.length})
          </button>
        </div>

        {/* Content */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Templates List */}
          <div className="md:col-span-2 space-y-4">
            {/* Add New Button */}
            <button
              onClick={() => openCreateModal(activeTab)}
              className={`w-full p-4 border-2 border-dashed rounded-xl flex items-center justify-center gap-2 ${
                activeTab === 'email' 
                  ? 'border-[#F4ACB7] text-[#E88D9B] hover:bg-[#FFE5D9]/30' 
                  : 'border-green-300 text-green-600 hover:bg-green-50'
              }`}
            >
              <Plus className="w-5 h-5" />
              <span>Create New {activeTab === 'email' ? 'Email' : 'WhatsApp'} Template</span>
            </button>

            {/* Templates Grid */}
            {loading ? (
              <div className="text-center py-8 text-gray-400">Loading...</div>
            ) : (
              <div className="space-y-3">
                {(activeTab === 'email' ? emailTemplates : whatsappTemplates).map(template => (
                  <div 
                    key={template.id} 
                    className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800">{template.name}</h3>
                        {template.subject && (
                          <p className="text-sm text-gray-500 mt-1">Subject: {template.subject}</p>
                        )}
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">{template.body}</p>
                        {template.description && (
                          <p className="text-xs text-gray-400 mt-2">{template.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 ml-4">
                        <button
                          onClick={() => openEditModal(template)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(template.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {(activeTab === 'email' ? emailTemplates : whatsappTemplates).length === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    No {activeTab === 'email' ? 'email' : 'WhatsApp'} templates yet. Create one to get started!
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Placeholders Reference */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 h-fit sticky top-4">
            <h3 className="font-semibold text-gray-800 mb-3">Available Placeholders</h3>
            <p className="text-xs text-gray-500 mb-4">
              Use these placeholders in your templates. They will be automatically replaced with booking data.
            </p>
            <div className="space-y-4 max-h-[500px] overflow-y-auto">
              {PLACEHOLDER_GROUPS.map(grp => (
                <div key={grp.group}>
                  <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">{grp.group}</h4>
                  <div className="space-y-1">
                    {grp.items.map(p => (
                      <div 
                        key={p.code}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer group"
                        onClick={() => copyPlaceholder(p.code)}
                      >
                        <div>
                          <code className="text-xs text-[#E88D9B] font-mono">{p.code}</code>
                          <p className="text-xs text-gray-500">{p.description}</p>
                        </div>
                        <Copy className="w-4 h-4 text-gray-400 group-hover:text-[#E88D9B]" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className={`p-4 flex items-center justify-between ${
                formData.type === 'email' ? 'bg-[#F4ACB7]' : 'bg-green-500'
              } text-white rounded-t-xl`}>
                <h3 className="font-bold text-lg">
                  {editingTemplate ? 'Edit' : 'Create'} {formData.type === 'email' ? 'Email' : 'WhatsApp'} Template
                </h3>
                <button onClick={() => setShowModal(false)} className="p-1 hover:bg-white/20 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F4ACB7]"
                    placeholder="e.g., Registration Complete, Delivery Confirmation"
                  />
                </div>

                {formData.type === 'email' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F4ACB7]"
                      placeholder="Email subject line"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message Body *</label>
                  <textarea
                    value={formData.body}
                    onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F4ACB7]"
                    placeholder={`Dear {{customer_name}},\n\nYour vehicle registration is complete.\n\nRegistration No: {{registration_no}}\nDelivery Date: {{delivery_date}}\n\nThank you for choosing us!`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F4ACB7]"
                    placeholder="Brief description of when to use this template"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className={`flex-1 px-4 py-2 text-white rounded-lg ${
                      formData.type === 'email' ? 'bg-[#F4ACB7] hover:bg-[#E88D9B]' : 'bg-green-500 hover:bg-green-600'
                    }`}
                  >
                    {editingTemplate ? 'Update' : 'Create'} Template
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </SalesLayout>
  );
}

export default SalesTemplateManagement;
